import {
  CollapseDS,
  SidePanelDS,
  Spacer,
  TextAreaDS,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { formatMileage } from "../../../../../libSlice/materialUiFormats";
import type { QueryStatus } from "../../../../../utils";
import type {
  VehicleRestitutionPictures,
  VehicleSessionRestitution,
} from "../../fleetApi";
import { CleaningTag } from "../cleaningTag";
import { RestitutionPictures } from "./RestitutionPictures";
import "../../../../../i18n/i18n";

export type RestitutionWithPictures = VehicleSessionRestitution & {
  picturesUrls: VehicleRestitutionPictures | null;
  picturesStatus: QueryStatus;
};

export const RestitutionVehicleState = (props: {
  finalRestitution?: RestitutionWithPictures;
  collapsedInitialRestitution?: RestitutionWithPictures;
}) => {
  const { finalRestitution, collapsedInitialRestitution } = props;
  const { t } = useTranslation();

  return (
    <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
      <SectionTitle>
        <TextCapitalized>
          {t("fleet.vehicleSessions.sidepanel.state.title")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={2} />
      <RestitutionState restitution={finalRestitution} />
      {collapsedInitialRestitution && (
        <>
          <Spacer y={2} />
          <CollapseDS
            label={t("fleet.vehicleSessions.sidepanel.state.seeInitial")}
          >
            <Spacer y={1} />
            <RestitutionState restitution={collapsedInitialRestitution} />
          </CollapseDS>
        </>
      )}
    </SidePanelDS.Section>
  );
};

const RestitutionState = (props: { restitution?: RestitutionWithPictures }) => {
  const { restitution } = props;
  const { t } = useTranslation();

  return (
    <>
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.state.mileage")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {restitution ? formatMileage(restitution.mileage) : "-"}
            </TextCapitalized>
          </StyledValue>
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.state.isCabinCleaned")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          restitution ? (
            <CleaningTag isCleaned={restitution.isCabinCleaned} />
          ) : (
            <StyledValue>-</StyledValue>
          )
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.state.isTrunkCleaned")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          restitution ? (
            <CleaningTag isCleaned={restitution.isTrunkCleaned} />
          ) : (
            <StyledValue>-</StyledValue>
          )
        }
      />
      <Spacer y={2} />
      <SectionTitle>
        <TextCapitalized>
          {t("fleet.vehicleSessions.sidepanel.state.observations")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={0.5} />
      <TextAreaDS
        value={restitution?.observations ?? ""}
        $update={() => {}}
        isDisabled={true}
      />
      <Spacer y={2} />
      <SectionTitle>
        <TextCapitalized>
          {t("fleet.vehicleSessions.sidepanel.state.pictures")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={0.5} />
      <RestitutionPictures
        pictures={restitution?.picturesUrls ?? null}
        picturesStatus={restitution?.picturesStatus ?? null}
      />
    </>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledValue = styled.div`
  text-align: right;
`;
